import { Injectable, NgZone, OnDestroy, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';

// Owais, 27 Oct 2026 (same rule as OneGate's server, lib/session-lifetime.js): never signed out before 12 h from sign-in; after
// 12 h, signed out after 15 min without genuine activity. Activity never starts a new 12 h. Only real clicks/keys/scrolls count
// (shared across this browser's tabs); background calls never do. The server enforces it; this only warns in time.
const MIN_SESSION_MS = 12 * 60 * 60 * 1000;
const IDLE_AFTER_MIN_MS = 15 * 60 * 1000;
const WARNING_DURATION_MS = 3 * 60 * 1000;
const REPORT_EVERY_MS = 60 * 1000;
const SHARED_KEY = 'og-last-activity';
const ACTIVITY_EVENTS = ['click', 'keydown', 'pointerdown', 'scroll', 'touchstart', 'wheel'];

/** When this session ends at the latest: 12 h after sign-in, or 15 min after the last activity, whichever is later. */
export function signOutAt(startedAt: number, lastActivityAt: number): number {
  return Math.max(startedAt + MIN_SESSION_MS, Math.max(startedAt, lastActivityAt) + IDLE_AFTER_MIN_MS);
}

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  readonly warningOpen = signal(false);
  readonly remainingSeconds = signal(WARNING_DURATION_MS / 1000);
  readonly remainingLabel = computed(() => {
    const total = Math.max(0, this.remainingSeconds());
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  });

  private readonly authSub: Subscription;
  private listening = false;
  private authenticated = false;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private warningDeadline = 0;
  private lastActivityAt = Date.now();
  private lastReportAt = 0;

  constructor() {
    this.authSub = this.auth.isAuthenticated$.subscribe((authenticated) => {
      this.authenticated = authenticated;
      if (authenticated) {
        this.start();
      } else {
        this.stopTimers();
      }
    });
  }

  ngOnDestroy(): void {
    this.stop();
    this.authSub.unsubscribe();
  }

  staySignedIn(): void {
    this.stopTimers();
    this.warningOpen.set(false);

    firstValueFrom(this.auth.refresh({ redirectOnFailure: false }))
      .then((token) => {
        if (!token) {
          this.auth.handleSessionExpired(undefined, 'session-expired');
          return;
        }
        this.authenticated = true;
        this.noteActivity(true);
        this.scheduleWarning();
      })
      .catch(() => this.auth.handleSessionExpired(undefined, 'session-expired'));
  }

  logoutNow(): void {
    void firstValueFrom(this.auth.logout('logout'));
  }

  private start(): void {
    if (typeof window === 'undefined') return;
    if (!this.listening) {
      this.zone.runOutsideAngular(() => {
        ACTIVITY_EVENTS.forEach((eventName) => {
          window.addEventListener(eventName, this.handleActivity, { passive: true });
        });
      });
      this.listening = true;
    }
    this.scheduleWarning();
  }

  private stop(): void {
    if (typeof window !== 'undefined' && this.listening) {
      ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, this.handleActivity));
    }
    this.listening = false;
    this.stopTimers();
  }

  private readonly handleActivity = (): void => {
    if (!this.authenticated || this.warningOpen()) return;
    this.noteActivity(false);
    this.scheduleWarning();
  };

  /** A genuine interaction: shared with the other tabs and reported to OneGate at most once a minute. */
  private noteActivity(force: boolean): void {
    const now = Date.now();
    this.lastActivityAt = now;
    try { localStorage.setItem(SHARED_KEY, String(now)); } catch { /* storage blocked: this tab only */ }
    if (force || now - this.lastReportAt >= REPORT_EVERY_MS) {
      this.lastReportAt = now;
      this.zone.run(() => { void firstValueFrom(this.auth.reportActivity()); });
    }
  }

  private latestActivity(): number {
    let shared = 0;
    try { shared = Number(localStorage.getItem(SHARED_KEY)) || 0; } catch { /* ignore */ }
    return Math.max(this.lastActivityAt, shared);
  }

  private scheduleWarning(): void {
    this.stopTimers();
    if (!this.authenticated || typeof window === 'undefined') return;
    const started = this.auth.sessionStartedAt() ?? this.lastActivityAt;
    const deadline = signOutAt(started, this.latestActivity());
    this.warningTimer = window.setTimeout(() => {
      // Another tab may have been used meanwhile: check again before warning.
      if (signOutAt(started, this.latestActivity()) > deadline) { this.scheduleWarning(); return; }
      this.zone.run(() => this.openWarning());
    }, Math.max(0, deadline - WARNING_DURATION_MS - Date.now()));
  }

  private openWarning(): void {
    if (!this.authenticated) return;
    this.warningOpen.set(true);
    this.remainingSeconds.set(WARNING_DURATION_MS / 1000);
    this.warningDeadline = Date.now() + WARNING_DURATION_MS;

    this.countdownTimer = setInterval(() => {
      const remaining = Math.ceil((this.warningDeadline - Date.now()) / 1000);
      this.remainingSeconds.set(Math.max(0, remaining));
    }, 1000);

    this.logoutTimer = setTimeout(() => this.expireSession(), WARNING_DURATION_MS);
  }

  private expireSession(): void {
    this.stopTimers();
    // Used in another tab during the countdown: keep going.
    const started = this.auth.sessionStartedAt() ?? this.lastActivityAt;
    if (signOutAt(started, this.latestActivity()) > Date.now() + WARNING_DURATION_MS) { this.warningOpen.set(false); this.scheduleWarning(); return; }
    this.warningOpen.set(false);
    this.authenticated = false;
    // Only this session ends (OneGate refuses its next refresh); a sign-out button still ends every session.
    this.auth.handleSessionExpired(this.router.url, 'idle-timeout');
  }

  private stopTimers(): void {
    if (this.warningTimer) clearTimeout(this.warningTimer);
    if (this.logoutTimer) clearTimeout(this.logoutTimer);
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.warningTimer = null;
    this.logoutTimer = null;
    this.countdownTimer = null;
  }
}
